import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginAdmin, clearAuthError } from "../store/auth-slice";

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loginStatus, error, token } = useSelector((state) => state.auth);

    const [form, setForm] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (token) navigate("/", { replace: true });
    }, [token, navigate]);

    useEffect(() => {
        return () => dispatch(clearAuthError());
    }, [dispatch]);

    const validate = () => {
        const errors = {};
        if (!form.email.trim()) errors.email = "Email is required.";
        else if (!/\S+@\S+\.\S+/.test(form.email)) errors.email = "Enter a valid email address.";
        if (!form.password) errors.password = "Password is required.";
        return errors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (fieldErrors[name]) setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }
        dispatch(loginAdmin(form));
    };

    const isLoading = loginStatus === "loading";

    return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-body-tertiary">
            <div className="w-100" style={{ maxWidth: 440 }}>
                {/* Logo / Brand */}
                <div className="text-center mb-4">
                    <h3 className="fw-bold mb-1">Formlyze</h3>
                    <p className="text-muted mb-0 small">Admin Dashboard</p>
                </div>

                {/* Card */}
                <div className="card border-0 shadow-sm rounded-4 p-4">
                    <div className="card-body p-2">
                        <h5 className="fw-semibold mb-1">Sign in</h5>
                        <p className="text-muted small mb-4">Enter your admin credentials to continue.</p>

                        {error && (
                            <div className="alert alert-danger py-2 small rounded-3" role="alert">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} noValidate>
                            {/* Email */}
                            <div className="mb-3">
                                <label htmlFor="email" className="form-label opacity-75 small fw-medium">
                                    Email address
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    className={`form-control${fieldErrors.email ? " is-invalid" : ""}`}
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="admin@example.com"
                                    autoComplete="email"
                                    disabled={isLoading}
                                />
                                {fieldErrors.email && (
                                    <div className="invalid-feedback">{fieldErrors.email}</div>
                                )}
                            </div>

                            {/* Password */}
                            <div className="mb-4">
                                <label htmlFor="password" className="form-label opacity-75 small fw-medium">
                                    Password
                                </label>
                                <div className="input-group">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        name="password"
                                        className={`form-control${fieldErrors.password ? " is-invalid" : ""}`}
                                        value={form.password}
                                        onChange={handleChange}
                                        placeholder="••••••••"
                                        autoComplete="current-password"
                                        disabled={isLoading}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowPassword((v) => !v)}
                                        tabIndex={-1}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                    >
                                        {showPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                                <line x1="1" y1="1" x2="23" y2="23"/>
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                <circle cx="12" cy="12" r="3"/>
                                            </svg>
                                        )}
                                    </button>
                                    {fieldErrors.password && (
                                        <div className="invalid-feedback">{fieldErrors.password}</div>
                                    )}
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary w-100 fw-medium"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"/>
                                        Signing in...
                                    </>
                                ) : (
                                    "Sign in"
                                )}
                            </button>
                        </form>
                    </div>
                </div>

                <p className="text-center text-muted small mt-4 mb-0">
                    Formlyze &copy; {new Date().getFullYear()} — Admin access only
                </p>
            </div>
        </div>
    );
};

export default Login;
